// The left column of «База знаний» (um/2026-10-30/03 п.2): the plain tree of the section («Статьи» with their views), then the parent
// «Разделы» with the sections of the base on branches and their counts — the column that used to stand in the body.

import { useMemo } from 'react';
import type { MenuSection } from '@confdeck/core-shared';
import { Branches, NavRow, SideNav, navCss, navigate, useFolder, useLocation, useMeta, useQuery, useRecord, useSync, type NavActive } from '@confdeck/core-web';
import { NO_SECTION, SORT, TAXONOMY_KEY, TYPE_KEY, sectionHref, sectionOf } from './sections.ts';

/** The section the address points at: the chosen one, that of the article a link opens, or the first of the base. */
export function useSection(): string {
  const meta = useMeta();
  const { search } = useLocation();
  const linked = useRecord(search.get('section') ? null : search.get('article') || null);
  const tax = meta.taxonomies().find((t) => t.key === TAXONOMY_KEY);
  const first = tax ? meta.children(null, tax.id)[0]?.id : undefined;
  return search.get('section') ?? (linked ? sectionOf(linked) : undefined) ?? first ?? NO_SECTION;
}

export function KnowledgeNav({ section, active }: { section: MenuSection; active: NavActive }) {
  const meta = useMeta();
  const client = useSync();
  const { path } = useLocation();
  const here = path === '/s/knowledge';
  const cur = useSection();
  const [open, flip] = useFolder('kb:sections');
  const type = meta.typeByKey(TYPE_KEY);
  const tax = meta.taxonomies().find((t) => t.key === TAXONOMY_KEY);
  const q = useQuery(type?.id ?? '', null, SORT, '');
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const id of q.ids) { const r = client.store.record(id); if (r) m.set(sectionOf(r), (m.get(sectionOf(r)) ?? 0) + 1); }
    return m;
  }, [q.ids, client]);
  const terms = tax ? meta.children(null, tax.id) : [];
  const count = (id: string) => <span className={navCss.navCount}>{counts.get(id) ?? 0}</span>;
  const dot = (color: string) => <span className={navCss.termDot} style={{ background: `var(--c-${color}-dot)` }} />;
  return (
    <>
      <SideNav section={section} active={active} hideScreens />
      <div className={navCss.navNode}>
        <NavRow icon="tag" label="Разделы" onClick={flip} toggle={{ open, onToggle: flip, label: 'Разделы' }} />
        {open && (
          <Branches>
            {terms.map((t) => <NavRow key={t.id} label={<>{dot(t.color)}{t.name}</>} on={here && cur === t.id} count={count(t.id)} onClick={() => navigate(sectionHref(t.id))} />)}
            <NavRow label={<>{dot('gray')}Без раздела</>} on={here && cur === NO_SECTION} count={count(NO_SECTION)} onClick={() => navigate(sectionHref(NO_SECTION))} />
          </Branches>
        )}
      </div>
    </>
  );
}

/** The head of the column on the base: the chosen section. */
export function useKnowledgeTitle(): string | null {
  const meta = useMeta();
  const { path } = useLocation();
  const cur = useSection();
  if (path !== '/s/knowledge') return null;
  return cur === NO_SECTION ? 'Без раздела' : meta.term(cur)?.name ?? 'Разделы';
}
